import React, { useState } from 'react';
import { BsDownload } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Flex } from '@chakra-ui/react';

import AddNewButton from '../../../../components/AddNewButton';
import DropdownMenu from '../../../../components/DropdownMenu';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import SaveChangesModal from '../../../../components/SaveChangesModal';
import SearchGlobalSearchInput from '../../../../components/search/SearchGlobalSearchInput';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { CypressIds } from '../../../../constants/cypressIds';
import { Statuses } from '../../../../constants/statuses';
import { exportListInsuranceRequirementsToExcel, selectJobProfile } from '../../../../redux/modules/jobs';
import { hideModal, showModal } from '../../../../redux/modules/modal';
import { hideTray, showTray, updateTrayProps } from '../../../../redux/modules/tray';
import { jobDetailsSearch } from '../../../../redux/reducer';
import { statusOptions } from '../../util';
import AddInsuredsToProjectTray from '../AddInsuredsToProjectTray';
import JobIconButton from '../JobIconButton';

import RecordSortOrderButton from './RecordSortOrderButton';

const JobInsuredsPanelHeader = () => {
  const pageData = useSelector(jobDetailsSearch.selectPageInfo);
  const dispatch = useDispatch();
  const { companyId, jobId } = useParams();
  const jobProfile = useSelector(selectJobProfile);
  const getSearchQueryParams = useSelector(jobDetailsSearch.selectRequestParamsFunction);

  const [selectedOption, setSelectedOption] = useState(statusOptions.active);

  const handleStatusFilterClick = selectedItem => {
    const selected = selectedItem.value;

    if (selected === 'all') {
      dispatch(jobDetailsSearch.setBaseFilters([
        { key: 'job.id', value: jobId }
      ]));

      setSelectedOption(statusOptions.all);
    } else if (selected === 'active') {
      dispatch(jobDetailsSearch.setBaseFilters([
        { key: 'job.id', value: jobId },
        { key: 'status.keyword', value: ['approved', 'not_approved'] }
      ]));

      setSelectedOption(statusOptions.active);
    } else {
      dispatch(jobDetailsSearch.setBaseFilters([
        { key: 'job.id', value: jobId },
        { key: 'status.keyword', value: statusOptions[selected].search.value }
      ]));

      setSelectedOption(statusOptions[selected]);
    }

    dispatch(jobDetailsSearch.getPage());
  };

  const handleExportInsuranceRequirements = () => {
    const searchParams = getSearchQueryParams({ scriptScore: true });

    if (!searchParams) return false;

    dispatch(exportListInsuranceRequirementsToExcel(jobProfile.jobId, searchParams));
  };

  const showExitModal = formTrayProps => {
    if (formTrayProps.formIsDirty) {
      dispatch(showModal(() => (
        <SaveChangesModal changedInfo="project or insured information" />
      ), {
        title: 'Exit without Saving?',
        buttons: {
          component: StackedButtonGroup,
          primaryButtonOptions: {
            onClick: () => {
              dispatch(hideModal());
              dispatch(hideTray());
            },
            dataAnalyticsId: 'cancel-insured-creation'
          }
        }
      }));
    } else {
      dispatch(hideTray());
    }

    dispatch(updateTrayProps({}));
  };

  const handleAddInsuredTray = () => {
    dispatch(showTray(() => (<AddInsuredsToProjectTray companyId={companyId} />), {
      size: 'sm',
      title: 'Create Record(s)',
      placement: 'right',
      overlayClose: false,
      params: {},
      onClick: () => {},
      onClose: showExitModal
    }));
  };

  return (
    <Flex justify="space-between" mb={6} w="100%">
      <Flex justify="flex-start" mr={4} w="60%">
        <SearchGlobalSearchInput
          dataAnalyticsId={analyticsIds.projectDetailsPage.searchInsuranceRequirements}
          mr={4}
          searchBlueprint={jobDetailsSearch}
          variant="secondary"
          width="300px"
        />
        <RecordSortOrderButton />
        {jobProfile?.stats?.activeInsureds > 0 && (
          <DropdownMenu
            dataAnalyticsKey={analyticsIds.projectDetailsPage.filterByInsuranceRequirementStatus}
            idKey="id"
            labelKey="displayName"
            options={Object.values(statusOptions)}
            selected={selectedOption.displayName}
            variant="secondary"
            width="215px"
            onSelect={handleStatusFilterClick}
          />
        )}
      </Flex>
      <Flex justifyContent="flex-end" w="40%">
        <AddNewButton
          data-cy={CypressIds.projectDetailsCreateRecordButton}
          dataAnalyticsId={analyticsIds.projectDetailsPage.addInsuredToProjectButton}
          isDisabled={jobProfile.status === Statuses.Archived}
          tabName="Create Record"
          onClick={handleAddInsuredTray}
        />
        {pageData.total > 0 && (
          <JobIconButton
            icon={BsDownload}
            label="Download Project Details"
            marginLeft={4}
            onClick={handleExportInsuranceRequirements}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default JobInsuredsPanelHeader;
